import { debounce, requestIdleCallback, viewTransition } from '@theme/utilities';
import { Component } from '@theme/component';

const OFFSET = 40;

/**
 * A custom element that manages a floating panel.
 */
export class FloatingPanelComponent extends Component {
  #updatePosition = async () => {
    // Wait for any view transitions to finish
    if (viewTransition.current) await viewTransition.current;

    // Clear prior adjustments so we measure the CSS default position.
    // Must unset `right` too — CSS often sets `right: 0`, and an inline
    // `left` alone is ignored while `right` remains (common RTL clip).
    this.style.left = '';
    this.style.right = '';
    this.style.top = `${OFFSET}px`;

    const rect = this.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const parent = this.offsetParent;

    if (!(parent instanceof Element)) {
      this.#mutationObserver.takeRecords();
      return;
    }

    const parentRect = parent.getBoundingClientRect();
    let desiredLeft = rect.left;

    if (rect.right > viewportWidth - OFFSET) {
      desiredLeft = viewportWidth - OFFSET - rect.width;
    }

    if (desiredLeft < OFFSET) {
      desiredLeft = OFFSET;
    }

    // Only write inline offsets when the panel would otherwise overflow.
    if (desiredLeft !== rect.left) {
      this.style.right = 'auto';
      this.style.left = `${desiredLeft - parentRect.left}px`;
    }

    this.#mutationObserver.takeRecords();
  };

  #mutationObserver = new MutationObserver(this.#updatePosition);

  #resizeListener = debounce(() => {
    const parent = this.closest('details');
    const closeOnResize = this.dataset.closeOnResize === 'true';
    if (parent instanceof HTMLDetailsElement && closeOnResize) {
      parent.open = false;
      parent.removeAttribute('open');
      this.#updatePosition();
    }
  }, 100);

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('resize', this.#resizeListener);

    requestIdleCallback(() => {
      this.#updatePosition();
      this.#mutationObserver.observe(this, { attributes: true });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#mutationObserver.disconnect();
  }
}

if (!customElements.get('floating-panel-component')) {
  customElements.define('floating-panel-component', FloatingPanelComponent);
}
